import React from 'react';

import {redirectForDownload} from 'BenchlingUtil';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import runModal from 'ui-library/runModal';

import CopyPublicLink from '../swagger-ui/CopyPublicLink';
import Filter from '../swagger-ui/Filter';
import Sidebar from '../swagger-ui/Sidebar';
import TryItOutButton from '../swagger-ui/TryItOutButton';
import {getSpecUrl} from '../swagger-ui/util';
import SwaggerRedirectModal from '../SwaggerRedirectModal';

interface BenchlingLayoutPlugin {
  wrapComponents: {
    info: () => () => null;
    FilterContainer: () => () => null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    operations: (Original: JSX.Element, system: any) => (props: any) => JSX.Element;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    TryItOutButton: (Original: JSX.Element, system: any) => (props: any) => JSX.Element;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    JumpToPath: (Original: JSX.Element, system: any) => (props: any) => JSX.Element;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    curl: (Original: JSX.Element, system: any) => (props: any) => JSX.Element;
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function BenchlingLayoutPlugin(system: any): BenchlingLayoutPlugin {
  //  BenchlingLayoutPlugin changes the default Swagger UI. It does 3 things:
  //  1. Adds a sidebar with links to each section
  //  2. Adds a custom filter bar
  //  3. Remove the default filter bar and default info component.
  //  4. Adds a copyPublicLink icon to the end of each operation summary bar.

  return {
    wrapComponents: {
      // Returning null removes the default components.
      info: () => () => null,
      // We add a custom filter below.
      FilterContainer: () => () => null,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      TryItOutButton: (_Original: JSX.Element, system: any) => (props: any) => (
        <div>
          <TryItOutButton {...props} userSignedIn={window.bootstrapUser != null} />
        </div>
      ),
      // JumpToPath is an unused component exposed for the plugin system.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      JumpToPath: (Original: JSX.Element, system: any) => (props: any) => (
        <>
          {/* @ts-expect-error */}
          <Original {...props} />
          {/* JumpToPath is also rendered in next to schemas with an undefined path */}
          {props.path !== undefined && (
            <span>
              <CopyPublicLink system={system} operationPath={props.path} />
            </span>
          )}
        </>
      ),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      curl: (Original: JSX.Element, system: any) => (props: any) => (
        <>
          {/* @ts-expect-error */}
          <Original {...props} />
          <div className="text-info u-tiny-spacing-top u-moderate-spacing-bottom">
            Note: This request will only work for a few minutes, after which the Authorization header must be
            replaced with an API key or fresh Bearer Token. See docs.benchling.com for details.
          </div>
        </>
      ),
      operations: (Original, system) => (props) => (
        <div id="custom-operations">
          <div id="left-float-sticky">
            <Sidebar system={system} />
          </div>
          <div id="right-float">
            <div className={`filter-flex-container ${window.bootstrapUser == null ? '' : 'full-filter-bar'}`}>
              <Filter
                value={system.layoutSelectors.currentFilter()}
                updateValue={system.layoutActions.updateFilter}
              />
              {window.bootstrapUser == null && (
                <div className="test-api-button-container">
                  <Button
                    text="Test APIs"
                    buttonStyle={ButtonStyle.SECONDARY}
                    size={ButtonSize.SMALL}
                    onClick={() => {
                      runModal(SwaggerRedirectModal, {});
                    }}
                  />
                </div>
              )}
              <div className="openapi-spec-button-container">
                <Button
                  text="OpenAPI Spec"
                  buttonStyle={ButtonStyle.SECONDARY}
                  size={ButtonSize.SMALL}
                  onClick={() => {
                    redirectForDownload(getSpecUrl());
                  }}
                />
              </div>
            </div>
            {/* @ts-expect-error */}
            <Original {...props} />
          </div>
        </div>
      ),
    },
  };
}
